import Content from "../Layout/Content";
import InvoicesStripePayModal from "./InvoicesStripePayModal";
import { useState, useMemo, useEffect } from "react";
import { Box, Stack, Typography, Button, Paper, useMediaQuery, useTheme } from "@mui/material";
import { getUsersTimeZone } from "../../utils/timeZoneUtils";
import { useInvoices } from "./useInvoices";
import InvoiceItem from "./InvoiceItem";
import LoadingOverlay from "../Shared/LoadingOverlay";
import NoInvoices from "./NoInvoices";
import InvoiceFilterDropdown, { InvoiceFilter } from "./InvoiceFilterDropdown";
import { useClientPortalDispatch, useClientPortalSelector } from "../../redux";
import { selectInvoice, openInvoicePayStripeDialog, closeInvoicePayStripeDialog } from "./invoiceSlice";
import { useClientPortalConfigContext } from "../../context/ClientPortalConfig.ctx";
import { useAnalytics } from "../../hooks/useAnalytics";

export default function Invoices() {
    const dispatch = useClientPortalDispatch();
    const { clientPortalConfig } = useClientPortalConfigContext();
    const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>("all");
    const { showDialog: showStripePayDialog } = useClientPortalSelector(selectInvoice);
    const { uniquePayee, invoices, hasUnpaidInvoices, isLoading, unpaidInvoicesIds } = useInvoices();
    const shouldShowPayAllButton =
        hasUnpaidInvoices && invoiceFilter !== "paid" && uniquePayee && !clientPortalConfig.isStripeFeeEnabled;
    const { track } = useAnalytics();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        track("page_view", { page_title: "Invoices" });
    }, [track]);

    const filteredInvoices = useMemo(() => {
        if (!invoices) return [];

        switch (invoiceFilter) {
            case "paid":
                return invoices.filter((invoice) => invoice.isPaid);
            case "unpaid":
                return invoices.filter((invoice) => !invoice.isPaid);
            default:
                return invoices;
        }
    }, [invoices, invoiceFilter]);

    const handleFilterChange = (value: InvoiceFilter) => {
        setInvoiceFilter(value);
    };

    const openPayAllDialog = () => {
        const unpaidInvoices = invoices.filter((invoice) => unpaidInvoicesIds.includes(invoice.id));
        if (unpaidInvoices.length === 0) {
            return;
        }
        const amountOwingDecimal = unpaidInvoices.reduce((total, invoice) => total + invoice.amountOwingDecimal, 0);
        const { practitioner } = unpaidInvoices[0];

        track("invoice_pay_all_clicked", {
            invoice_count: unpaidInvoicesIds.length,
            total_amount: amountOwingDecimal,
        });

        dispatch(
            openInvoicePayStripeDialog({
                practitionerStripeAccountId: practitioner.stripeAccount,
                invoiceIds: unpaidInvoicesIds,
                practitionerId: practitioner.practitionerId,
                amount: amountOwingDecimal,
            })
        );
    };

    const handleClose = () => {
        dispatch(closeInvoicePayStripeDialog());
    };

    return (
        <Content dataTestIdSuffix="invoices">
            {isLoading ? (
                <LoadingOverlay />
            ) : (
                <Stack spacing={2} p={isMobile ? 2 : 4}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "8px",
                        }}
                    >
                        <Stack>
                            <Typography variant="h6" fontWeight="bold">
                                Invoices
                            </Typography>
                            <Typography variant="caption"> All times are in {getUsersTimeZone()} </Typography>
                        </Stack>
                        <Box sx={{ alignSelf: "center", marginLeft: "auto" }}>
                            {shouldShowPayAllButton && (
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={openPayAllDialog}
                                    data-test-id="button_pay_all_invoices"
                                >
                                    Pay All
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {invoices && invoices.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: isMobile ? "flex-start" : "center",
                            }}
                        >
                            <Typography variant="body2" fontWeight="bold">
                                Filter Invoices to Display:
                            </Typography>
                            <InvoiceFilterDropdown value={invoiceFilter} onChange={handleFilterChange} />
                        </Box>
                    )}

                    <Paper>
                        <Stack spacing={2} p={3}>
                            {filteredInvoices.length > 0 ? (
                                <Stack spacing={1} sx={{ overflow: "hidden", minWidth: 0 }}>
                                    {filteredInvoices.map((invoice) => (
                                        <InvoiceItem
                                            key={invoice.id}
                                            invoice={invoice}
                                            hasUnpaidInvoices={hasUnpaidInvoices}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <NoInvoices />
                            )}
                        </Stack>
                    </Paper>
                </Stack>
            )}

            {showStripePayDialog && <InvoicesStripePayModal handleClose={handleClose} />}
        </Content>
    );
}
